import { isEqual } from 'lodash'

const VIEW = 'personAccessViews.'

const enrolmentOf = row => ({
  campus: row[`${VIEW}campusName`] ?? null,
  gradeLevel: row[`${VIEW}academicYearEnrollment.section.gradeLevelId`] ?? null,
  gradeSection: row[`${VIEW}academicYearEnrollment.section.gradeSection`] ?? null,
  notification: row[`${VIEW}schoolEnrollment.person.contacts.notification`] ?? null,
  student: [row[`${VIEW}schoolEnrollment.person.firstName`], row[`${VIEW}schoolEnrollment.person.lastName`]]
    .filter(Boolean)
    .join(' '),
})

// The lookup returns one flat row per person, enrolment and phone
export const groupRecipients = rows => {
  const people = new Map()

  for (const row of rows ?? []) {
    const id = String(row.id)
    if (!people.has(id)) {
      const fields = Object.entries(row).filter(([key]) => !key.includes('.'))
      people.set(id, { ...Object.fromEntries(fields), jobTitle: row['jobTitle.name'] ?? null, phones: [], enrolments: [] })
    }
    const person = people.get(id)

    const phone = { number: row['phones.number'], type: row['phones.type'] }
    if (phone.number && !person.phones.some(item => item.number === phone.number)) person.phones.push(phone)

    const enrolment = enrolmentOf(row)
    if (!person.enrolments.some(item => isEqual(item, enrolment))) person.enrolments.push(enrolment)
  }

  return [...people.values()]
}

const enrolmentsOf = person => person?.enrolments ?? []

// `notification` lists the channels a parent gets for a student; null means it was never set
export const optedOut = (person, channel) => {
  const preferences = enrolmentsOf(person).map(enrolment => enrolment.notification)
  return preferences.length > 0 && preferences.every(list => Array.isArray(list) && !list.includes(channel))
}

export const campusesOf = person =>
  [...new Set(enrolmentsOf(person).map(enrolment => enrolment.campus).filter(Boolean))]

export const gradeLevelsOf = person =>
  [...new Set(enrolmentsOf(person).map(enrolment => enrolment.gradeLevel).filter(Boolean))]

export const gradeSectionsOf = person =>
  [...new Set(enrolmentsOf(person).map(enrolment => enrolment.gradeSection).filter(Boolean))]

export const matchesAny = (values, selected) =>
  !selected?.length || values.some(value => selected.includes(value))

export const GRADE_FILTERED_TYPES = ['parent', 'student']

const matches = (enrolment, { campuses, gradeLevels, gradeSections }) =>
  (!campuses.length || campuses.includes(enrolment.campus)) &&
  (!gradeLevels.length || gradeLevels.includes(enrolment.gradeLevel)) &&
  (!gradeSections.length || gradeSections.includes(enrolment.gradeSection))

export const filterRecipients = (people, filters = {}) => {
  const { campuses = [], gradeLevels = [], gradeSections = [] } = filters

  if (!campuses.length && !gradeLevels.length && !gradeSections.length) return people ?? []

  return (people ?? []).filter(person => {
    if (!GRADE_FILTERED_TYPES.includes(person?.type)) return matchesAny(campusesOf(person), campuses)

    return enrolmentsOf(person).some(enrolment => matches(enrolment, { campuses, gradeLevels, gradeSections }))
  })
}

export const gradeLevelRank = (gradeLevels = []) => {
  const ranks = new Map((gradeLevels ?? []).map((level, index) => [level?.id, index]))
  return id => (ranks.has(id) ? ranks.get(id) : Number.MAX_SAFE_INTEGER)
}

const compareNames = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true })

export const orderGradeLevels = (ids, levelOrder) => {
  const rankOf = gradeLevelRank(levelOrder)
  return [...(ids ?? [])].sort((a, b) => rankOf(a) - rankOf(b) || compareNames(a, b))
}

export const optionsFrom = (people, extract) =>
  [...new Set((people ?? []).flatMap(extract))].filter(Boolean).sort((a, b) => String(a).localeCompare(String(b)))

const allEnrolments = people => (people ?? []).flatMap(enrolmentsOf)

export const campusesFor = people =>
  [...new Set(allEnrolments(people).map(enrolment => enrolment.campus).filter(Boolean))].sort(compareNames)

export const gradeLevelsFor = (people, campuses = [], levelOrder) => {
  const levels = allEnrolments(people)
    .filter(enrolment => !campuses.length || campuses.includes(enrolment.campus))
    .map(enrolment => enrolment.gradeLevel)
    .filter(Boolean)

  return orderGradeLevels([...new Set(levels)], levelOrder)
}

export const sectionsFor = (people, campuses = [], gradeLevels = [], levelOrder) => {
  const pairs = new Map()

  for (const enrolment of allEnrolments(people)) {
    if (!enrolment.gradeSection) continue
    if (campuses.length && !campuses.includes(enrolment.campus)) continue
    if (gradeLevels.length && !gradeLevels.includes(enrolment.gradeLevel)) continue

    pairs.set(enrolment.gradeSection, enrolment.gradeLevel)
  }

  const rankOf = gradeLevelRank(levelOrder)

  return [...pairs.keys()].sort(
    (a, b) => rankOf(pairs.get(a)) - rankOf(pairs.get(b)) || compareNames(a, b),
  )
}

export const pruneSelection = (available, selected) => {
  const allowed = new Set(available)
  return (selected ?? []).filter(value => allowed.has(value))
}

// The enrolments that put the person in the filtered list, e.g. only the children in the chosen grades
export const enrolmentsFor = (person, filters = {}) => {
  const { campuses = [], gradeLevels = [], gradeSections = [] } = filters
  const all = enrolmentsOf(person)

  if (!campuses.length && !gradeLevels.length && !gradeSections.length) return all

  return all.filter(enrolment => matches(enrolment, { campuses, gradeLevels, gradeSections }))
}

export const listOf = values => [...new Set(values.filter(Boolean))].join(', ')

export const sentenceOf = values => {
  const unique = [...new Set(values.filter(Boolean))]
  return unique.length > 1 ? `${unique.slice(0, -1).join(', ')} and ${unique[unique.length - 1]}` : unique[0] ?? ''
}

// One entry per child, keeping each child's grade next to their name
export const studentsOf = (enrolments, gradeLabel) => {
  const children = new Map()
  for (const enrolment of enrolments) {
    if (!enrolment.student || children.has(enrolment.student)) continue

    const grade = [enrolment.gradeLevel && gradeLabel(enrolment.gradeLevel), enrolment.gradeSection].filter(Boolean)
    children.set(enrolment.student, grade.length ? `${enrolment.student} (${grade.join(', ')})` : enrolment.student)
  }
  return sentenceOf([...children.values()])
}

export const mobilesOf = person => [
  ...new Set(
    (person?.phones ?? [])
      .filter(phone => String(phone?.type ?? '').toLowerCase() === 'mobile')
      .map(phone => String(phone?.number ?? '').trim())
      .filter(Boolean),
  ),
]

export const addressesOf = (person, channel) =>
  channel === 'sms' ? mobilesOf(person) : [person?.email].filter(Boolean)

export const reachable = (people, channel) =>
  (people ?? []).filter(person => !optedOut(person, channel) && addressesOf(person, channel).length > 0)
